import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import Card from '@components-clean/core/card';
import { getEventRateSubtitleComponents, getPropOTInfoText } from '@components-clean/core/charts/scatter-plot/utils';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import PanelFrame from '@components-clean/core/panel-frame';
import { useCountrySelectorContext } from '@contexts-clean/country-selector-context';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { CM2Dataset, PanelResult } from '@interfaces/central-monitoring';
import { EventRatesData, ProportionsOTData } from '@interfaces-clean/panels.interface';
import { PanelTheme, PanelType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { GET_CM2_PANEL } from '@queries/central-monitoring';
import {
    cm2Panel,
    cm2PanelVariables,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_graph_PanelGraph as PanelGraph,
} from '@queries/generated/central-monitoring.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

type PanelProps = {
    studyRef: string;
    dataset: CM2Dataset;
    panelRef: string;
    dataViewRef?: string;
    chartTitle?: string;
};

interface ErrorPanelProps {
    message: string;
    testId: string;
    title?: string | null;
    type?: 'Info' | 'Error';
}

const ErrorPanel = ({ message, testId, title, type = 'Info' }: ErrorPanelProps) => {
    return (
        <Card title={title} data-testid={testId} padding="none">
            <InfoField message={message} type={type} layout="center" />
        </Card>
    );
};

type DisplayControlsHubPanelProps = {
    panelType: PanelType | null;
    panelTheme: PanelTheme | null;
    isPreview?: boolean;
    graph: PanelGraph | null;
    testId: string;
    panelConfig?: getCm2PanelsDataview_OpraQueries_cm2panels_Panel | null;
    panelData?: PanelResult;
    dataViewRef?: string;
    countryValue?: string;
};

const DisplayControlsHubPanel = ({
    panelType,
    graph,
    testId,
    panelTheme,
    panelConfig,
    panelData,
    isPreview = false,
    dataViewRef,
    countryValue,
}: DisplayControlsHubPanelProps) => {
    const theme = panelTheme ?? undefined;
    const { legendToggleValue } = usePageToolbar();
    const commonProps = {
        'data-testid': testId,
        title: panelConfig?.plotTitle,
        theme,
        xAxisTitle: graph?.xTitle ?? undefined,
        yAxisTitle: graph?.yTitle ?? undefined,
        showLegend: isPreview ? true : legendToggleValue?.current,
        version: panelData!.version!,
    };

    const { state } = useLocalState();
    const { t } = useTranslation();

    switch (panelType) {
        case PanelType.PROPORTIONS_OVER_TIME:
            const propOTData = panelData?.proportionsOT as ProportionsOTData;

            const isPropOTCountry = propOTData.countries && Object.keys(propOTData.countries).length === 1;

            const propOTBenchmarkValue = isPropOTCountry
                ? propOTData.countries![Object.keys(propOTData.countries || {})[0]].benchmarkValue
                : propOTData.study?.benchmarkValue;

            const propOTBenchmarkType = isPropOTCountry
                ? panelConfig?.proportionsOverTimeParameters?.countryBenchmark
                : panelConfig?.proportionsOverTimeParameters?.benchmarkType;

            const propOTThresholdType = panelConfig?.proportionsOverTimeParameters?.thresholdType;

            const propOTCommonProps = {
                ...commonProps,
                xAxisTitle: panelConfig?.proportionsOverTimeParameters?.xAxisTitle ?? undefined,
                yAxisTitle: panelConfig?.proportionsOverTimeParameters?.yAxisTitle ?? undefined,
            };

            return (
                <PanelFrame
                    {...propOTCommonProps}
                    panelType={panelType}
                    panelData={propOTData}
                    modelConfig={{
                        proportionsOTBenchmarkType: propOTBenchmarkType,
                        proportionsOTThresholdType: propOTThresholdType,
                        numeratorName: panelConfig?.proportionsOverTimeParameters?.numeratorName,
                        denominatorName: panelConfig?.proportionsOverTimeParameters?.denominatorName,
                        plotCalculation: panelConfig?.proportionsOverTimeParameters?.cumulativeOrTimePeriod,
                        plotBy: panelConfig?.proportionsOverTimeParameters?.plotBy,
                        minSubjects: panelConfig?.proportionsOverTimeParameters?.minData,
                        upperRed: panelConfig?.proportionsOverTimeParameters?.upperRed,
                        upperAmber: panelConfig?.proportionsOverTimeParameters?.upperAmber,
                        lowerRed: panelConfig?.proportionsOverTimeParameters?.lowerRed,
                        lowerAmber: panelConfig?.proportionsOverTimeParameters?.lowerAmber,
                    }}
                    infoText={getPropOTInfoText(
                        propOTBenchmarkType,
                        propOTBenchmarkValue,
                        propOTThresholdType,
                        panelConfig?.proportionsOverTimeParameters?.cumulativeOrTimePeriod,
                        panelConfig?.proportionsOverTimeParameters?.plotBy,
                        panelConfig?.proportionsOverTimeParameters?.upperAmber,
                        panelConfig?.proportionsOverTimeParameters?.lowerAmber,
                        panelConfig?.proportionsOverTimeParameters?.upperRed,
                        panelConfig?.proportionsOverTimeParameters?.lowerRed,
                        panelConfig?.proportionsOverTimeParameters?.minData,
                    )}
                    popoutInfo={
                        dataViewRef
                            ? {
                                  dataCutDate: state?.selectedCM2Dataset?.dataCutDate,
                                  comparisonDataCutDate: state?.selectedComparisonCM2Dataset?.dataCutDate,
                                  dataviewRef: dataViewRef,
                                  showPopoutIcon: true,
                              }
                            : undefined
                    }
                    hidePanelSetup
                    className="k-h-full"
                />
            );
        case PanelType.EVENT_RATES_OVER_TIME:
            const eventChartData = panelData?.eventRatesOverTime as EventRatesData;

            const eventRateCommonProps = {
                ...commonProps,
                xAxisTitle: panelConfig?.eventRatesOverTimeParameters?.xAxisTitle ?? undefined,
                yAxisTitle: panelConfig?.eventRatesOverTimeParameters?.yAxisTitle ?? undefined,
            };

            // Look at the country filter selector to see if we should use country values
            const isCountry = countryValue !== undefined;
            const hasCountryData = eventChartData.countryData && Object.keys(eventChartData.countryData).length > 0;

            const benchmarkValue = isCountry
                ? hasCountryData
                    ? eventChartData.countryData![Object.keys(eventChartData.countryData || {})[0]].benchmark
                    : null
                : eventChartData.studyBenchmark;

            const benchmarkType = isCountry
                ? panelConfig?.eventRatesOverTimeParameters?.countryBenchmark
                : panelConfig?.eventRatesOverTimeParameters?.benchmarkType;

            return (
                <PanelFrame
                    panelType={PanelType.EVENT_RATES_OVER_TIME}
                    {...eventRateCommonProps}
                    panelData={eventChartData}
                    modelConfig={{
                        eventRateBenchmarkType: benchmarkType,
                        eventRateThresholdType: panelConfig?.eventRatesOverTimeParameters?.thresholdType,
                        plotBy: panelConfig?.eventRatesOverTimeParameters?.plotBy,
                        plotCalculation: panelConfig?.eventRatesOverTimeParameters?.cumulativeOrTimePeriod,
                        upperRed: panelConfig?.eventRatesOverTimeParameters?.upperRed,
                        upperAmber: panelConfig?.eventRatesOverTimeParameters?.upperAmber,
                        lowerRed: panelConfig?.eventRatesOverTimeParameters?.lowerRed,
                        lowerAmber: panelConfig?.eventRatesOverTimeParameters?.lowerAmber,
                        minSubjects: panelConfig?.eventRatesOverTimeParameters?.minSubjects,
                        numeratorName: panelConfig?.eventRatesOverTimeParameters?.numeratorName,
                        denominatorName: panelConfig?.eventRatesOverTimeParameters?.denominatorName,
                        minTimeOnStudy: {
                            unit: panelConfig?.eventRatesOverTimeParameters?.minTimeOnStudyUnit || null,
                            period: panelConfig?.eventRatesOverTimeParameters?.minTimeOnStudyPeriod || null,
                        },
                    }}
                    infoText={getEventRateSubtitleComponents(
                        benchmarkType,
                        benchmarkValue,
                        panelConfig?.eventRatesOverTimeParameters?.thresholdType,
                        panelConfig?.eventRatesOverTimeParameters?.cumulativeOrTimePeriod,
                        panelConfig?.eventRatesOverTimeParameters?.plotBy,
                        panelConfig?.eventRatesOverTimeParameters?.upperAmber,
                        panelConfig?.eventRatesOverTimeParameters?.lowerAmber,
                        panelConfig?.eventRatesOverTimeParameters?.upperRed,
                        panelConfig?.eventRatesOverTimeParameters?.lowerRed,
                        panelConfig?.eventRatesOverTimeParameters?.minSubjects,
                        {
                            period: panelConfig?.eventRatesOverTimeParameters?.minTimeOnStudyPeriod,
                            unit: panelConfig?.eventRatesOverTimeParameters?.minTimeOnStudyUnit,
                        },
                    )}
                    popoutInfo={
                        dataViewRef
                            ? {
                                  // Event Rate is CM2 only so this should be safe
                                  dataCutDate: state?.selectedCM2Dataset?.dataCutDate,
                                  comparisonDataCutDate: state?.selectedComparisonCM2Dataset?.dataCutDate,
                                  dataviewRef: dataViewRef,
                                  showPopoutIcon: true,
                              }
                            : undefined
                    }
                    hidePanelSetup
                    className="k-h-full"
                />
            );
        default:
            return <ErrorPanel message={t('cm:componentsClean.panels.configError')} testId={testId} />;
    }
};

const ControlsHubChart = ({ studyRef, dataset, panelRef, dataViewRef, chartTitle }: PanelProps) => {
    const { t } = useTranslation();

    const { country } = useCountrySelectorContext();

    const { data: panelConfig, loading: loadingConfig } = useQuery<cm2Panel, cm2PanelVariables>(GET_CM2_PANEL, {
        variables: { panelId: panelRef, timestamp: dataset.created },
    });

    const {
        data: panelData,
        isLoading: loadingPanel,
        isValidating: reloadingPanel,
        error,
        mutate,
    } = ControlsHubApi.useGetPanelData(
        studyRef,
        dataset.id,
        panelRef,
        country.value,
        panelConfig?.cm2Panel?.active ?? false,
    );

    useEffect(() => {
        // eslint-disable-next-line no-void
        if (panelConfig?.cm2Panel?.active) void mutate();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [country]);

    const testId = `panel-${panelRef}`;

    if (loadingPanel || reloadingPanel || loadingConfig) return <Loading />;

    // If config not available we need to render a card that matches the panel style but with a custom error
    if (!panelConfig?.cm2Panel)
        return <ErrorPanel message={t('cm:componentsClean.panels.configError')} testId={testId} />;

    if (!panelConfig?.cm2Panel.active)
        return (
            <ErrorPanel
                testId={testId}
                message={t('cm:componentsClean.panels.panelInactive')}
                title={panelConfig.cm2Panel.graph?.plotTitle}
            />
        );

    if (error) {
        return (
            <ErrorPanel
                message={t('cm:componentsClean.panels.panelError')}
                testId={testId}
                title={panelConfig.cm2Panel.graph?.plotTitle}
                type="Error"
            />
        );
    }

    const { panelType, graph, panelTheme } = panelConfig.cm2Panel;
    const config = chartTitle ? { ...panelConfig.cm2Panel, plotTitle: chartTitle } : panelConfig.cm2Panel;

    return (
        <DisplayControlsHubPanel
            panelType={panelType}
            panelTheme={panelTheme}
            graph={graph}
            testId={testId}
            panelConfig={config}
            panelData={panelData}
            dataViewRef={dataViewRef}
            countryValue={country.value}
        />
    );
};

export { ControlsHubChart, DisplayControlsHubPanel };
